import { useEffect, useState } from 'react';
import { listCollectorPhotos } from '@/api/tasks';
import { getCollectorMeta } from '@/utils/collectors';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { isPdfPhoto } from '@/utils/photoFiles';
import type { Collector, PhotoItem } from '@/types/odoo';

interface WerfCollectorPhotoStripProps {
  collector: Collector;
  projectName: string;
}

const STRIP_PREVIEW = 2; // thumbnails shown before the "+N" overflow tile

/**
 * Read-only photo strip for a collector. Loads existing collector photos from S3
 * (same proxy as the installer) and opens the shared lightbox on tap. NO upload /
 * delete controls — the magazijnier is read-only.
 */
export function WerfCollectorPhotoStrip({ collector, projectName }: WerfCollectorPhotoStripProps) {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    let active = true;
    // Guard: never fetch with a placeholder project name — S3 keys are derived
    // from it, so a wrong name 422s. Wait until the canonical name resolves.
    if (!projectName) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const meta = getCollectorMeta(collector, projectName);
    listCollectorPhotos(collector.id, meta)
      .then((r) => {
        if (active) setPhotos(r.photos);
      })
      .catch(() => {
        // Silently fail — strip just shows "geen foto's".
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [collector, projectName]);

  if (loading) {
    return (
      <div className="px-3.5 pb-3">
        <div className="w-5 h-5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
          <span className="sr-only">Foto&apos;s laden...</span>
        </div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="px-3.5 pb-3">
        <p className="text-[11px] text-td-light italic">Nog geen foto&apos;s</p>
      </div>
    );
  }

  // PhotoLightbox is an image viewer; a PDF opens in a new tab from its own tile.
  const imagePhotos = photos.filter((p) => !isPdfPhoto(p));
  const preview = photos.slice(0, STRIP_PREVIEW);
  const overflow = photos.length - preview.length;

  // "+N" should open at the first photo the preview did NOT already show. That
  // index has to be resolved against the image-only lightbox array, or PDFs
  // sitting in the preview slots push it past the intended photo. It is -1 when
  // everything behind "+N" is a PDF — nothing the image viewer can display, so
  // the button is disabled rather than opening an empty lightbox.
  const firstHiddenImage = photos.slice(STRIP_PREVIEW).find((p) => !isPdfPhoto(p));
  const overflowIndex = firstHiddenImage
    ? imagePhotos.findIndex((p) => p.key === firstHiddenImage.key)
    : -1;

  function open(index: number) {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }

  return (
    <div className="px-3.5 pb-3">
      <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted mb-1.5">
        Foto&apos;s vorige plaatsing ·{' '}
        <span className="font-semibold normal-case text-td-light">tik = volledig scherm</span>
      </p>
      <div className="flex gap-1.5 overflow-x-auto">
        {preview.map((p, idx) => (
          <PhotoGridThumb
            key={p.key}
            photo={p}
            tileSizeClass="w-16 h-16"
            imgClassName="w-16 h-16 rounded-lg object-cover border border-gray-200 hover:border-td-blue transition-colors"
            openLabel={`Foto ${idx + 1} openen`}
            onOpen={() => open(imagePhotos.findIndex((i) => i.key === p.key))}
          />
        ))}
        {overflow > 0 && (
          <button
            type="button"
            onClick={() => open(overflowIndex)}
            disabled={overflowIndex < 0}
            className="w-16 h-16 rounded-lg shrink-0 bg-td-navy/80 flex items-center justify-center hover:bg-td-navy transition-colors disabled:opacity-50"
            aria-label={`Nog ${overflow} foto's bekijken`}
          >
            <span className="text-[11px] font-bold text-white">+{overflow}</span>
          </button>
        )}
      </div>

      {lightboxOpen && imagePhotos.length > 0 && (
        <PhotoLightbox
          photos={imagePhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
